import { Show, createEffect, createSignal, on, onCleanup } from "solid-js";
import { Portal } from "solid-js/web";

import { useClient } from "@revolt/client";

import { ATLAS, GREET, GREET_RATIO, RECTS } from "./welcomeAssets";

/**
 * Majalis welcome moment: a short, skippable reward that plays once when the
 * app tour closes. The emblem bursts into Arabic words, the member is greeted
 * by name, then the dashboard cards cascade in underneath.
 * Kept light on purpose: low-resolution canvas, one draw per word, auto-stop on slow frames.
 */

const [runId, setRunId] = createSignal(0);
export const playWelcome = () => setRunId((n) => n + 1);

const sm = (a: number, b: number, x: number) => {
  x = (x - a) / (b - a);
  x = x < 0 ? 0 : x > 1 ? 1 : x;
  return x * x * (3 - 2 * x);
};
const S = (u: number) => 0.1 + 1.8 * Math.pow(u, 1.9);

let seed = 7;
const rnd = () => {
  seed = (seed * 16807) % 2147483647;
  return (seed - 1) / 2147483646;
};

let tints: HTMLCanvasElement[] | null = null;
let loading: Promise<void> | null = null;
function loadTints(): Promise<void> {
  if (loading) return loading;
  loading = new Promise((res) => {
    const im = new Image();
    im.onload = () => {
      tints = ["#F7B01A", "#FFD064"].map((col) => {
        const t = document.createElement("canvas");
        t.width = im.naturalWidth;
        t.height = im.naturalHeight;
        const c = t.getContext("2d")!;
        c.drawImage(im, 0, 0);
        c.globalCompositeOperation = "source-in";
        c.fillStyle = col;
        c.fillRect(0, 0, t.width, t.height);
        return t;
      });
      res();
    };
    im.onerror = () => res();
    im.src = ATLAS;
  });
  return loading;
}

/** Words fly outward from the centre, growing as they pass the viewer */
function runBurst(cv: HTMLCanvasElement): () => void {
  const ctx = cv.getContext("2d");
  if (!ctx || !tints) return () => {};
  const W = window.innerWidth;
  const H = window.innerHeight;
  const Q = Math.min(1, window.devicePixelRatio || 1) * (W < 700 ? 0.8 : 0.6);
  cv.width = Math.round(W * Q);
  cv.height = Math.round(H * Q);
  const vm = Math.min(W, H);
  const cx = W / 2;
  const cy = H / 2;
  const ASP = Math.min(2.1, (W / H) * 1.05);
  const N = W < 700 ? 16 : 30;
  const NS = W < 700 ? 90 : 200;

  const pieces = [];
  for (let i = 0; i < N; i++) {
    const born = rnd() * 0.7;
    pieces.push({
      r: RECTS[(rnd() * RECTS.length) | 0],
      a: rnd() * 6.2832,
      rb: vm * (0.3 + Math.pow(rnd(), 0.8) * 0.8),
      bw: (100 + Math.pow(rnd(), 1.2) * 110) * (vm / 760),
      rot: (rnd() - 0.5) * 0.4,
      born,
      dur: 0.95 + rnd() * 0.15,
      col: rnd() < 0.25 ? 1 : 0,
    });
  }
  const sparks = [];
  for (let j = 0; j < NS; j++)
    sparks.push({
      a: rnd() * 6.2832,
      rb: vm * (0.15 + rnd() * 0.9),
      born: rnd() * 0.8,
      dur: 0.45 + rnd() * 0.35,
    });

  let raf = 0;
  let stopped = false;
  let slow = 0;
  const t0 = performance.now();
  let last = t0;

  const tick = (now: number) => {
    if (stopped) return;
    const raw = (now - last) / 1000;
    last = now;
    const s = (now - t0) / 1000;
    if (raw > 0.1) slow++;
    else if (slow > 0 && raw < 0.05) slow--;
    ctx.setTransform(Q, 0, 0, Q, 0, 0);
    ctx.clearRect(0, 0, W, H);
    if (slow >= 4 || s > 2.3) return;

    ctx.globalCompositeOperation = "lighter";
    ctx.lineWidth = 1.4;
    ctx.lineCap = "round";
    ctx.strokeStyle = "rgba(250,178,26,0.8)";
    ctx.beginPath();
    for (const q of sparks) {
      const u = (s - q.born) / q.dur;
      if (u <= 0.02 || u > 1) continue;
      const r1 = q.rb * S(u);
      const r0 = q.rb * S(Math.max(0, u - 0.08));
      const co = Math.cos(q.a);
      const si = Math.sin(q.a);
      ctx.moveTo(cx + co * r0 * ASP, cy + si * r0);
      ctx.lineTo(cx + co * r1 * ASP, cy + si * r1);
    }
    ctx.stroke();

    ctx.globalCompositeOperation = "source-over";
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "low";
    for (let i = pieces.length - 1; i >= 0; i--) {
      const d = pieces[i];
      const u = (s - d.born) / d.dur;
      if (u <= 0 || u > 1) continue;
      const Sk = S(u);
      const rr = d.rb * Sk;
      const w = d.bw * Sk;
      const sc = w / d.r[2];
      const al = sm(0, 0.12, u) * (1 - sm(0.66, 0.97, u)) * (0.35 + 0.5 * Math.min(1, Sk / 0.9));
      if (al < 0.02) continue;
      ctx.save();
      ctx.translate(cx + Math.cos(d.a) * rr * ASP, cy + Math.sin(d.a) * rr);
      ctx.rotate(d.rot);
      ctx.scale(sc, sc);
      ctx.globalAlpha = al;
      ctx.drawImage(tints![d.col], d.r[0], d.r[1], d.r[2], d.r[3], -d.r[2] / 2, -d.r[3] / 2, d.r[2], d.r[3]);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => {
    stopped = true;
    cancelAnimationFrame(raf);
  };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const CASCADE_CSS =
  "@keyframes mjRise{from{opacity:0;transform:translateY(22px) scale(.98)}to{opacity:1;transform:none}}" +
  ".mj-cascade .mj-card{animation:mjRise .7s cubic-bezier(.2,.7,.2,1) both}" +
  [0, 1, 2, 3, 4, 5, 6, 7]
    .map((i) => ".mj-cascade .mj-card:nth-child(" + (i + 1) + "){animation-delay:" + (i * 0.09).toFixed(2) + "s}")
    .join("") +
  "@media (prefers-reduced-motion:reduce){.mj-cascade .mj-card{animation:none}}";

export function WelcomeMoment() {
  const client = useClient();
  const [visible, setVisible] = createSignal(false);
  const [leaving, setLeaving] = createSignal(false);
  const [burst, setBurst] = createSignal(false);
  const [textIn, setTextIn] = createSignal(false);
  const [name, setName] = createSignal("");
  const [tier, setTier] = createSignal("");
  let cv: HTMLCanvasElement | undefined;
  let timers: number[] = [];
  let stopBurst: (() => void) | undefined;
  let active = false;

  const clearAll = () => {
    timers.forEach((t) => window.clearTimeout(t));
    timers = [];
    if (stopBurst) stopBurst();
    stopBurst = undefined;
  };
  const later = (fn: () => void, ms: number) => timers.push(window.setTimeout(fn, ms));

  const leave = () => {
    if (!active || leaving()) return;
    setLeaving(true);
    try {
      if (!document.getElementById("mj-cascade-css")) {
        const st = document.createElement("style");
        st.id = "mj-cascade-css";
        st.textContent = CASCADE_CSS;
        document.head.appendChild(st);
      }
      document.documentElement.classList.add("mj-cascade");
      window.setTimeout(() => document.documentElement.classList.remove("mj-cascade"), 2200);
    } catch {
      /* ignore */
    }
    if (stopBurst) stopBurst();
    stopBurst = undefined;
    later(() => {
      active = false;
      setVisible(false);
      setLeaving(false);
      setBurst(false);
      setTextIn(false);
    }, 750);
  };

  const start = async () => {
    if (active) return;
    active = true;
    const u: any = client()?.user;
    setName(String(u?.displayName ?? u?.username ?? ""));
    setTier("Thank you for joining.");
    setLeaving(false);
    setBurst(false);
    setTextIn(false);
    setVisible(true);

    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      later(() => setTextIn(true), 150);
      later(leave, 1900);
      return;
    }
    await loadTints();
    later(() => {
      setBurst(true);
      try {
        navigator.vibrate?.(30);
      } catch {
        /* ignore */
      }
      if (cv) stopBurst = runBurst(cv);
    }, 450);
    later(() => setTextIn(true), 950);
    later(leave, 3000);
  };

  createEffect(
    on(
      runId,
      (n) => {
        if (n > 0) start();
      },
      { defer: true },
    ),
  );
  onCleanup(clearAll);

  return (
    <Show when={visible()}>
      <Portal>
        <div
          role="presentation"
          onClick={leave}
          style={
            "position:fixed;top:0;right:0;bottom:0;left:0;z-index:10002;cursor:pointer;overflow:hidden;background:rgba(8,8,10,0.95);transition:opacity .7s ease;opacity:" +
            (leaving() ? 0 : 1)
          }
        >
          <div
            aria-hidden="true"
            style={
              "position:absolute;left:50%;top:50%;width:min(130vmin,980px);aspect-ratio:1;transform:translate(-50%,-50%);background:radial-gradient(closest-side,rgba(255,160,0,0.34),rgba(255,160,0,0.1) 55%,rgba(255,160,0,0));transition:opacity 1s ease;opacity:" +
              (burst() ? 0.55 : 1)
            }
          />
          <canvas
            ref={cv}
            aria-hidden="true"
            style="position:absolute;top:0;left:0;width:100%;height:100%;"
          />
          <div
            aria-hidden="true"
            style={
              "position:absolute;left:50%;top:50%;width:min(34vmin,220px);aspect-ratio:974/853;background:linear-gradient(180deg,#FFC420,#E69500);-webkit-mask:url(/assets/web/logo.svg) center/contain no-repeat;mask:url(/assets/web/logo.svg) center/contain no-repeat;transition:transform .6s cubic-bezier(.2,.7,.2,1),opacity .5s ease;transform:translate(-50%,-50%) scale(" +
              (burst() ? 1.5 : 1) +
              ");opacity:" +
              (burst() ? 0 : 1)
            }
          />
          <div
            style={
              "position:absolute;left:0;right:0;top:50%;display:flex;flex-direction:column;align-items:center;text-align:center;gap:10px;padding:0 24px;color:#fff;transition:opacity .9s ease,transform .9s cubic-bezier(.2,.7,.2,1);opacity:" +
              (textIn() ? 1 : 0) +
              ";transform:translateY(" +
              (textIn() ? "-50%" : "-38%") +
              ");"
            }
          >
            <div style="font-size:12px;font-weight:700;letter-spacing:0.3em;padding-left:0.3em;text-transform:uppercase;color:#E2B04A;">
              Welcome to Majalis
            </div>
            <div style="font-size:clamp(30px,7vw,52px);font-weight:700;line-height:1.15;letter-spacing:-0.015em;text-shadow:0 2px 24px rgba(0,0,0,0.7);">
              {name() ? name() : "Welcome"}
            </div>
            <img
              src={GREET}
              alt="أهلاً وسهلاً"
              lang="ar"
              style={
                "width:min(200px,52vw);height:auto;aspect-ratio:1/" + GREET_RATIO +
                ";filter:drop-shadow(0 0 10px rgba(242,157,10,0.35));"
              }
            />
            <Show when={tier()}>
              <div style="font-size:15px;line-height:1.5;color:#c9c9d3;max-width:320px;margin-top:2px;text-shadow:0 1px 12px rgba(0,0,0,0.8);">
                {tier()}
              </div>
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
